import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Target, Users, Activity, PieChart as PieChartIcon, BarChart3 } from 'lucide-react';
import { backendClient } from '@/lib/backendClient';
import { Badge } from '@/components/ui/badge';

interface BusinessMetrics {
  // Growth metrics
  userGrowthRate: number;
  sessionGrowthRate: number;
  practitionerGrowthRate: number;

  // Engagement metrics
  avgSessionsPerUser: number;
  sessionCompletionRate: number;
  userRetentionRate: number;
  dailyActiveUsers: number;

  // Business metrics
  categoryPopularity: Array<{ name: string; sessions: number; growth: number; retention: number }>;
  userSegments: Array<{ segment: string; count: number; engagement: number }>;

  // Historical growth data (actual data, not forecasts)
  historicalGrowth: Array<{ month: string; users: number; sessions: number }>;
}

const BusinessIntelligence: React.FC = () => {
  const [metrics, setMetrics] = useState<BusinessMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBusinessMetrics();
  }, []);

  const fetchBusinessMetrics = async () => {
    try {
      setLoading(true);

      // Fetch all necessary data
      const [
        sessionsResult,
        usersResult,
        rolesResult,
        categoriesResult
      ] = await Promise.all([
        backendClient.from('sessions').select('*'),
        backendClient.from('profiles').select('*'),
        backendClient.from('user_roles').select('*'),
        backendClient.from('categories').select('*')
      ]);

      const sessions = sessionsResult.data || [];
      const users = usersResult.data || [];
      const roles = rolesResult.data || [];
      const categories = categoriesResult.data || [];

      // Calculate time periods
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Growth calculations
      const currentMonthUsers = users.filter(u => new Date(u.created_at) > lastMonth).length;
      const currentMonthSessions = sessions.filter(s => new Date(s.created_at) > lastMonth).length;
      const currentMonthPractitioners = roles.filter(r =>
        r.role === 'practitioner' && new Date(r.created_at) > lastMonth
      ).length;

      const previousMonthUsers = users.filter(u => {
        const date = new Date(u.created_at);
        return date > new Date(now.getFullYear(), now.getMonth() - 2, now.getDate()) && date <= lastMonth;
      }).length;

      const previousMonthSessions = sessions.filter(s => {
        const date = new Date(s.created_at);
        return date > new Date(now.getFullYear(), now.getMonth() - 2, now.getDate()) && date <= lastMonth;
      }).length;

      const previousMonthPractitioners = roles.filter(r => {
        const date = new Date(r.created_at);
        return r.role === 'practitioner' && date > new Date(now.getFullYear(), now.getMonth() - 2, now.getDate()) && date <= lastMonth;
      }).length;

      const userGrowthRate = previousMonthUsers > 0
        ? ((currentMonthUsers - previousMonthUsers) / previousMonthUsers) * 100
        : currentMonthUsers > 0 ? 100 : 0;

      const sessionGrowthRate = previousMonthSessions > 0
        ? ((currentMonthSessions - previousMonthSessions) / previousMonthSessions) * 100
        : currentMonthSessions > 0 ? 100 : 0;

      const practitionerGrowthRate = previousMonthPractitioners > 0
        ? ((currentMonthPractitioners - previousMonthPractitioners) / previousMonthPractitioners) * 100
        : currentMonthPractitioners > 0 ? 100 : 0;

      // Engagement metrics
      const totalSessionUsers = new Set(sessions.map(s => s.user_id)).size;
      const avgSessionsPerUser = totalSessionUsers > 0 ? sessions.length / totalSessionUsers : 0;
      const dailyActiveUsers = new Set(
        sessions.filter(s => new Date(s.created_at) > lastWeek).map(s => s.user_id)
      ).size;

      // Calculate REAL retention rate (users active in last 30 days vs total users created before that)
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const activeUserSet = new Set(
        sessions
          .filter(s => new Date(s.created_at) > thirtyDaysAgo)
          .map(s => s.user_id)
      );
      const usersCreatedBeforeThirtyDays = users.filter(u => new Date(u.created_at) < thirtyDaysAgo).length;
      const userRetentionRate = usersCreatedBeforeThirtyDays > 0
        ? Math.round((activeUserSet.size / usersCreatedBeforeThirtyDays) * 100)
        : 0;

      // Calculate REAL session completion rate (sessions with completed status)
      const completedSessions = sessions.filter(s =>
        s.status === 'completed' || s.status === 'success' || s.status === 'done'
      ).length;
      const sessionCompletionRate = sessions.length > 0
        ? Math.round((completedSessions / sessions.length) * 100)
        : 0;

      // Category analysis
      const categoryPopularity = categories.map(category => {
        const categorySessions = sessions.filter(s => s.category_id === category.id);
        const recentSessions = categorySessions.filter(s => new Date(s.created_at) > lastWeek);
        const oldSessions = categorySessions.filter(s => {
          const date = new Date(s.created_at);
          return date <= lastWeek && date > new Date(lastWeek.getTime() - 7 * 24 * 60 * 60 * 1000);
        });

        const growth = oldSessions.length > 0 
          ? ((recentSessions.length - oldSessions.length) / oldSessions.length) * 100 
          : recentSessions.length > 0 ? 100 : 0;

        // Calculate retention (users who came back for this category)
        const categoryUsers = new Set(categorySessions.map(s => s.user_id));
        const returningUsers = Array.from(categoryUsers).filter(userId => 
          categorySessions.filter(s => s.user_id === userId).length > 1
        ).length;
        const retention = categoryUsers.size > 0 ? (returningUsers / categoryUsers.size) * 100 : 0;

        return {
          name: category.name,
          sessions: categorySessions.length,
          growth: Math.round(growth),
          retention: Math.round(retention)
        };
      }).sort((a, b) => b.sessions - a.sessions);

      // User segments
      const practitionerIds = roles.filter(r => r.role === 'practitioner').map(r => r.user_id);
      const adminIds = roles.filter(r => r.role === 'admin').map(r => r.user_id);
      
      const userSegments = [
        {
          segment: 'Practitioners',
          count: practitionerIds.length,
          engagement: practitionerIds.length > 0 
            ? sessions.filter(s => practitionerIds.includes(s.user_id)).length / practitionerIds.length 
            : 0
        },
        {
          segment: 'Admin Users',
          count: adminIds.length,
          engagement: adminIds.length > 0 
            ? sessions.filter(s => adminIds.includes(s.user_id)).length / adminIds.length 
            : 0
        },
        {
          segment: 'Regular Users',
          count: users.length - practitionerIds.length - adminIds.length,
          engagement: users.length - practitionerIds.length - adminIds.length > 0 
            ? sessions.filter(s => !practitionerIds.includes(s.user_id) && !adminIds.includes(s.user_id)).length / (users.length - practitionerIds.length - adminIds.length)
            : 0
        }
      ];

      // Calculate REAL historical growth (last 12 months of actual data)
      const historicalGrowth = Array.from({ length: 12 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - 11 + i);
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);

        const monthUsers = users.filter(u => {
          const createdAt = new Date(u.created_at);
          return createdAt >= monthStart && createdAt <= monthEnd;
        }).length;

        const monthSessions = sessions.filter(s => {
          const createdAt = new Date(s.created_at);
          return createdAt >= monthStart && createdAt <= monthEnd;
        }).length;

        return {
          month: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          users: monthUsers,
          sessions: monthSessions
        };
      });

      setMetrics({
        userGrowthRate: Math.round(userGrowthRate),
        sessionGrowthRate: Math.round(sessionGrowthRate),
        practitionerGrowthRate: Math.round(practitionerGrowthRate),

        avgSessionsPerUser: Math.round(avgSessionsPerUser * 10) / 10,
        sessionCompletionRate: sessionCompletionRate,
        userRetentionRate: userRetentionRate,
        dailyActiveUsers,
        
        categoryPopularity,
        userSegments,

        historicalGrowth
      });

    } catch (error) {
      console.error('Error fetching business metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !metrics) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];

  return (
    <div className="space-y-6">
      {/* Key Business Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">User Growth</p>
                <p className="text-3xl font-bold text-green-900">{metrics.userGrowthRate}%</p>
                <p className="text-xs text-green-600 mt-1">vs last month</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Session Growth</p>
                <p className="text-3xl font-bold text-blue-900">{metrics.sessionGrowthRate}%</p>
                <p className="text-xs text-blue-600 mt-1">vs last month</p>
              </div>
              <Activity className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">User Retention</p>
                <p className="text-3xl font-bold text-purple-900">{metrics.userRetentionRate}%</p>
                <p className="text-xs text-purple-600 mt-1">30-day retention</p>
              </div>
              <Target className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Daily Active Users</p>
                <p className="text-3xl font-bold text-orange-900">{metrics.dailyActiveUsers}</p>
                <p className="text-xs text-orange-600 mt-1">last 7 days</p>
              </div>
              <Users className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Business Intelligence Tabs */}
      <Tabs defaultValue="growth" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="growth" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Growth
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="segments" className="flex items-center gap-2">
            <PieChartIcon className="h-4 w-4" />
            Segments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="growth" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Historical Growth (12 Months)
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Real growth data based on actual user registrations and session activity
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={metrics.historicalGrowth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="users"
                    stackId="1"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                    name="New Users"
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    stackId="2"
                    stroke="#82ca9d"
                    fill="#82ca9d"
                    fillOpacity={0.6}
                    name="Sessions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Category Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={metrics.categoryPopularity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="sessions" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Category Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {metrics.categoryPopularity.slice(0, 5).map((category, index) => (
                    <div key={category.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-medium">{category.name}</p>
                        <p className="text-sm text-muted-foreground">{category.sessions} sessions</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={category.growth >= 0 ? "default" : "destructive"}>
                          {category.growth >= 0 ? '+' : ''}{category.growth}% growth
                        </Badge>
                        <Badge variant="outline">
                          {category.retention}% retention
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="segments" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>User Segments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={metrics.userSegments}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ segment, count }) => `${segment}: ${count}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {metrics.userSegments.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>

                <div className="space-y-4">
                  {metrics.userSegments.map((segment, index) => (
                    <div key={segment.segment} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <div>
                          <p className="font-medium">{segment.segment}</p>
                          <p className="text-sm text-muted-foreground">{segment.count} users</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{Math.round(segment.engagement * 10) / 10}</p>
                        <p className="text-xs text-muted-foreground">avg sessions</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BusinessIntelligence;